import React, { useState } from 'react'
import { Calendar, Clock, Image, Hash, AtSign, Tag, Wand2, Eye, Send, AlertCircle, CheckCircle2, Camera, Upload } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Platform, SocialPostFormData, MediaItem } from '@/types/social'
import { useCreatePost, useProfiles, useGenerateContent, useBGVConnectionHealth } from '../hooks/useSocialMedia'
import { BGVGallerySelector, BGVSelectedImage } from '@/components/gallery/BGVGallerySelector'
import { BGVSelectedImages } from '@/components/gallery/BGVSelectedImages'
import { useTrackImageUsage } from '@/hooks/useBGVImageTracking'

interface PostCreateDialogProps {
  onClose: () => void
  initialData?: Partial<SocialPostFormData>
}

const PLATFORMS: Platform[] = ['INSTAGRAM', 'FACEBOOK', 'TWITTER', 'LINKEDIN']

const PLATFORM_CONFIGS: Record<Platform, {
  maxChars: number
  supportsImages: boolean
  supportsVideo: boolean
  supportsCarousel: boolean
  maxHashtags: number
  color: string
}> = {
  INSTAGRAM: {
    maxChars: 2200,
    supportsImages: true,
    supportsVideo: true,
    supportsCarousel: true,
    maxHashtags: 30,
    color: 'bg-gradient-to-r from-purple-500 to-pink-500'
  },
  FACEBOOK: {
    maxChars: 63206,
    supportsImages: true,
    supportsVideo: true,
    supportsCarousel: true,
    maxHashtags: 50,
    color: 'bg-blue-600'
  },
  TWITTER: {
    maxChars: 280,
    supportsImages: true,
    supportsVideo: true,
    supportsCarousel: false,
    maxHashtags: 10,
    color: 'bg-sky-400'
  },
  LINKEDIN: {
    maxChars: 3000,
    supportsImages: true,
    supportsVideo: true,
    supportsCarousel: false,
    maxHashtags: 20,
    color: 'bg-blue-700'
  },
  PINTEREST: {
    maxChars: 500,
    supportsImages: true,
    supportsVideo: true,
    supportsCarousel: false,
    maxHashtags: 20,
    color: 'bg-red-600'
  },
  TIKTOK: {
    maxChars: 300,
    supportsImages: false,
    supportsVideo: true,
    supportsCarousel: false,
    maxHashtags: 10,
    color: 'bg-black'
  }
}

export default function PostCreateDialog({ onClose, initialData }: PostCreateDialogProps) {
  const [formData, setFormData] = useState<SocialPostFormData>({
    content: initialData?.content || '',
    caption: initialData?.caption || '',
    media: initialData?.media || [],
    platforms: initialData?.platforms || ['INSTAGRAM'],
    scheduled_time: initialData?.scheduled_time || '',
    hashtags: initialData?.hashtags || [],
    mentions: initialData?.mentions || [],
    tags: initialData?.tags || []
  })

  const [newHashtag, setNewHashtag] = useState('')
  const [newMention, setNewMention] = useState('')
  const [newTag, setNewTag] = useState('')
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  // BGV Gallery Integration
  const [bgvImages, setBgvImages] = useState<BGVSelectedImage[]>([])
  const [showBGVGallery, setShowBGVGallery] = useState(false)

  // BGV Integration hooks
  const { data: profiles, isLoading: profilesLoading } = useProfiles()
  const { data: connectionHealth } = useBGVConnectionHealth()
  const createPostMutation = useCreatePost()
  const generateContentMutation = useGenerateContent()
  const trackImageUsage = useTrackImageUsage()

  const handlePlatformToggle = (platform: Platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }))
  }

  const addHashtag = () => {
    if (newHashtag && !formData.hashtags?.includes(newHashtag)) {
      setFormData(prev => ({
        ...prev,
        hashtags: [...(prev.hashtags || []), newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`]
      }))
      setNewHashtag('')
    }
  }

  const removeHashtag = (hashtag: string) => {
    setFormData(prev => ({
      ...prev,
      hashtags: prev.hashtags?.filter(h => h !== hashtag) || []
    }))
  }

  const addMention = () => {
    if (newMention && !formData.mentions?.includes(newMention)) {
      setFormData(prev => ({
        ...prev,
        mentions: [...(prev.mentions || []), newMention.startsWith('@') ? newMention : `@${newMention}`]
      }))
      setNewMention('')
    }
  }

  const removeMention = (mention: string) => {
    setFormData(prev => ({
      ...prev,
      mentions: prev.mentions?.filter(m => m !== mention) || []
    }))
  }

  const addTag = () => {
    if (newTag && !formData.tags?.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag) || []
    }))
  }

  const handleBGVImagesSelected = (images: BGVSelectedImage[]) => {
    setBgvImages(images)
    
    // Convert BGV images to MediaItem format for form data
    const mediaItems: MediaItem[] = images.map((image, index) => ({
      id: image.id,
      url: image.url,
      type: 'IMAGE',
      alt_text: image.alt_text || image.caption,
      position: index
    }))
    
    setFormData(prev => ({
      ...prev,
      media: [...prev.media.filter(m => !m.id.startsWith('bgv-')), ...mediaItems]
    }))
  }

  const handleRemoveBGVImage = (imageId: string) => {
    setBgvImages(prev => prev.filter(img => img.id !== imageId))
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter(m => m.id !== imageId)
    }))
  }

  const handleReorderBGVImages = (reorderedImages: BGVSelectedImage[]) => {
    setBgvImages(reorderedImages)
    
    // Update media array with new order
    const reorderedMedia: MediaItem[] = reorderedImages.map((image, index) => ({
      id: image.id,
      url: image.url,
      type: 'IMAGE',
      alt_text: image.alt_text || image.caption,
      position: index
    }))
    
    setFormData(prev => ({
      ...prev,
      media: [
        ...prev.media.filter(m => !bgvImages.some(bgv => bgv.id === m.id)),
        ...reorderedMedia
      ]
    }))
  }

  const generateAIContent = async () => {
    try {
      const result = await generateContentMutation.mutateAsync({
        prompt: 'Create engaging social media content for FixMyBrick masonry services',
        context: {
          platforms: formData.platforms,
          existing_content: formData.content
        }
      })

      setFormData(prev => ({
        ...prev,
        content: result.content,
        hashtags: [...(prev.hashtags || []), ...result.hashtags]
      }))
    } catch (error) {
      console.error('Failed to generate AI content:', error)
    }
  }

  const validateForm = (): boolean => {
    const errors: string[] = []

    if (!formData.content.trim()) {
      errors.push('Content is required')
    }

    if (formData.platforms.length === 0) {
      errors.push('At least one platform must be selected')
    }

    // Check character limits for selected platforms
    formData.platforms.forEach(platform => {
      if (isCharacterLimitExceeded(platform)) {
        errors.push(`Content exceeds character limit for ${platform}`)
      }
    })

    // Check if profiles exist for selected platforms
    if (profiles) {
      formData.platforms.forEach(platform => {
        const profileExists = profiles.some(p => p.platform === platform && p.is_active)
        if (!profileExists) {
          errors.push(`No active profile found for ${platform}`)
        }
      })
    }

    setValidationErrors(errors)
    return errors.length === 0
  }

  const handleSubmit = async (action: 'draft' | 'schedule' | 'publish') => {
    if (!validateForm()) {
      return
    }

    try {
      // Update form data based on action
      const submitData = { ...formData }
      
      if (action === 'schedule' && !submitData.scheduled_time) {
        setValidationErrors(['Scheduled time is required for scheduling posts'])
        return
      }

      if (action === 'draft') {
        submitData.scheduled_time = undefined
      }

      // Create the post
      const result = await createPostMutation.mutateAsync(submitData)
      
      // Track BGV image usage if any BGV images were selected
      if (bgvImages.length > 0 && result.id) {
        try {
          await trackImageUsage.mutateAsync({
            images: bgvImages,
            socialPostId: result.id,
            platform: formData.platforms.join(','), // Multiple platforms as comma-separated
            action: 'create'
          })
          
          console.log(`Successfully tracked usage for ${bgvImages.length} BGV images`)
        } catch (trackingError) {
          console.error('Failed to track BGV image usage:', trackingError)
          // Don't fail the post creation if tracking fails
        }
      }

      onClose()
    } catch (error) {
      console.error('Failed to submit post:', error)
      setValidationErrors(['Failed to create post. Please try again.'])
    }
  }

  const getCharacterCount = (platform: Platform) => {
    const config = PLATFORM_CONFIGS[platform]
    return formData.content.length
  }

  const isCharacterLimitExceeded = (platform: Platform) => {
    const config = PLATFORM_CONFIGS[platform]
    return formData.content.length > config.maxChars
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Create Social Media Post</h2>
          <p className="text-muted-foreground">
            Compose and schedule your post across multiple platforms
          </p>
        </div>
        <Button
          variant="outline"
          onClick={generateAIContent}
          disabled={generateContentMutation.isPending}
        >
          <Wand2 className="h-4 w-4 mr-2" />
          {generateContentMutation.isPending ? 'Generating...' : 'AI Assist'}
        </Button>
      </div>

      {/* Connection Status Alert */}
      {connectionHealth && connectionHealth.status !== 'healthy' && (
        <Alert variant={connectionHealth.status === 'unhealthy' ? 'destructive' : 'default'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            BGV Connection: {connectionHealth.message}
            {connectionHealth.status === 'unhealthy' && ' Some features may not be available.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="compose" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="compose">Compose</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="space-y-4">
          {/* Platform Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Select Platforms</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {PLATFORMS.map(platform => {
                  const config = PLATFORM_CONFIGS[platform]
                  const isSelected = formData.platforms.includes(platform)
                  const charCount = getCharacterCount(platform)
                  const charLimit = config.maxChars
                  const isOverLimit = isCharacterLimitExceeded(platform)
                  
                  // Check if profile exists for this platform
                  const hasProfile = profiles?.some(p => p.platform === platform && p.is_active)
                  const isDisabled = profilesLoading || !hasProfile

                  return (
                    <div
                      key={platform}
                      className={`
                        relative p-4 rounded-lg border-2 cursor-pointer transition-all
                        ${isSelected 
                          ? 'border-primary bg-primary/5' 
                          : isDisabled
                          ? 'border-border bg-muted/20 cursor-not-allowed'
                          : 'border-border hover:border-primary/50'
                        }
                      `}
                      onClick={() => !isDisabled && handlePlatformToggle(platform)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${config.color}`} />
                          <span className={`font-medium ${isDisabled ? 'text-muted-foreground' : ''}`}>
                            {platform}
                          </span>
                          {!hasProfile && !profilesLoading && (
                            <AlertCircle className="h-3 w-3 text-destructive" />
                          )}
                        </div>
                        <Checkbox 
                          checked={isSelected} 
                          disabled={isDisabled}
                          onChange={() => !isDisabled && handlePlatformToggle(platform)}
                        />
                      </div>
                      {isSelected && (
                        <div className="mt-2 text-xs">
                          <span className={isOverLimit ? 'text-destructive' : 'text-muted-foreground'}>
                            {charCount}/{charLimit} characters
                          </span>
                        </div>
                      )}
                      {!hasProfile && !profilesLoading && (
                        <div className="mt-2 text-xs text-destructive">
                          No active profile
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Content Composition */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Post Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="content">Main Content</Label>
                <Textarea
                  id="content"
                  placeholder="What's happening? Share your story..."
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="min-h-[120px] resize-none"
                />
              </div>

              <div>
                <Label htmlFor="caption">Caption (Optional)</Label>
                <Textarea
                  id="caption"
                  placeholder="Add a detailed caption..."
                  value={formData.caption}
                  onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                  className="min-h-[80px] resize-none"
                />
              </div>

              {/* Hashtags */}
              <div>
                <Label>Hashtags</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="relative flex-1">
                    <Hash className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Add hashtag"
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addHashtag()}
                      className="pl-8"
                    />
                  </div>
                  <Button onClick={addHashtag} size="sm">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.hashtags?.map(hashtag => (
                    <Badge 
                      key={hashtag} 
                      variant="secondary" 
                      className="cursor-pointer"
                      onClick={() => removeHashtag(hashtag)}
                    >
                      {hashtag} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Mentions */}
              <div>
                <Label>Mentions</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="relative flex-1">
                    <AtSign className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Add mention"
                      value={newMention}
                      onChange={(e) => setNewMention(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addMention()}
                      className="pl-8"
                    />
                  </div>
                  <Button onClick={addMention} size="sm">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.mentions?.map(mention => (
                    <Badge 
                      key={mention} 
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => removeMention(mention)}
                    >
                      {mention} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label>Tags</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="relative flex-1">
                    <Tag className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Add tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                      className="pl-8"
                    />
                  </div>
                  <Button onClick={addTag} size="sm">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.tags?.map(tag => (
                    <Badge 
                      key={tag} 
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => removeTag(tag)}
                    >
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="media" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Media Library</CardTitle>
              <CardDescription>
                Select images from BGV galleries or upload new media
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Media Selection Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <Camera className="h-10 w-10 mx-auto text-primary mb-3" />
                  <p className="text-sm font-medium mb-2">BGV Gallery</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Select approved images from BGV galleries
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowBGVGallery(true)}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Browse BGV Gallery
                  </Button>
                </div>

                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm font-medium mb-2">Upload Media</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Upload new images or videos
                  </p>
                  <Button variant="outline" size="sm" disabled>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Files
                  </Button>
                </div>
              </div>

              {/* BGV Selected Images */}
              {bgvImages.length > 0 && (
                <BGVSelectedImages
                  images={bgvImages}
                  onRemoveImage={handleRemoveBGVImage}
                  onReorderImages={handleReorderBGVImages}
                  maxImages={10}
                />
              )}

              {/* Regular Media Items */}
              {formData.media.filter(item => !bgvImages.some(bgv => bgv.id === item.id)).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Uploaded Media</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {formData.media
                        .filter(item => !bgvImages.some(bgv => bgv.id === item.id))
                        .map((item, index) => (
                        <div key={item.id} className="relative group">
                          <img
                            src={item.url}
                            alt={item.alt_text || `Media ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button 
                            className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                media: prev.media.filter(m => m.id !== item.id)
                              }))
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Media Summary */}
              {(bgvImages.length > 0 || formData.media.length > 0) && (
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                  <div className="flex items-center gap-4">
                    <span>
                      Total: {formData.media.length} media item{formData.media.length !== 1 ? 's' : ''}
                    </span>
                    {bgvImages.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {bgvImages.length} from BGV
                      </Badge>
                    )}
                  </div>
                  {formData.media.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setBgvImages([])
                        setFormData(prev => ({ ...prev, media: [] }))
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Publishing Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="schedule-date">Schedule Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="schedule-date"
                      type="date"
                      className="pl-8"
                      value={formData.scheduled_time?.split('T')[0] || ''}
                      onChange={(e) => {
                        const currentTime = formData.scheduled_time?.split('T')[1] || '12:00'
                        setFormData(prev => ({
                          ...prev,
                          scheduled_time: e.target.value ? `${e.target.value}T${currentTime}` : ''
                        }))
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="schedule-time">Schedule Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="schedule-time"
                      type="time"
                      className="pl-8"
                      value={formData.scheduled_time?.split('T')[1]?.substring(0, 5) || ''}
                      onChange={(e) => {
                        const currentDate = formData.scheduled_time?.split('T')[0] || new Date().toISOString().split('T')[0]
                        setFormData(prev => ({
                          ...prev,
                          scheduled_time: `${currentDate}T${e.target.value}:00`
                        }))
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Queue Assignment</Label>
                <Select 
                  value={formData.queue_id || ''} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, queue_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a queue (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily Posts</SelectItem>
                    <SelectItem value="promotional">Promotional Content</SelectItem>
                    <SelectItem value="educational">Educational Posts</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {profiles && profiles.length > 0 && (
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Available Profiles</h4>
                  <div className="space-y-2">
                    {profiles.filter(p => formData.platforms.includes(p.platform)).map(profile => (
                      <div key={profile.id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            profile.connection_status === 'connected' ? 'bg-green-500' : 'bg-red-500'
                          }`} />
                          <span>{profile.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {profile.platform}
                          </Badge>
                        </div>
                        <span className="text-muted-foreground capitalize">
                          {profile.connection_status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Optimal Posting Times</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Instagram:</span> 9AM, 3PM, 7PM
                  </div>
                  <div>
                    <span className="text-muted-foreground">Facebook:</span> 8AM, 12PM, 5PM
                  </div>
                  <div>
                    <span className="text-muted-foreground">Twitter:</span> 9AM, 12PM, 3PM
                  </div>
                  <div>
                    <span className="text-muted-foreground">LinkedIn:</span> 8AM, 12PM, 6PM
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Post Preview</CardTitle>
              <CardDescription>
                Preview how your post will appear on different platforms
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.platforms.map(platform => (
                  <div key={platform} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-3 h-3 rounded-full ${PLATFORM_CONFIGS[platform].color}`} />
                      <span className="font-medium">{platform}</span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm">{formData.content}</p>
                      {formData.caption && (
                        <p className="text-xs text-muted-foreground">{formData.caption}</p>
                      )}
                      {formData.hashtags && formData.hashtags.length > 0 && (
                        <p className="text-xs text-blue-600">
                          {formData.hashtags.join(' ')}
                        </p>
                      )}
                      {formData.media.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {formData.media.slice(0, 4).map((item, index) => (
                            <img
                              key={item.id}
                              src={item.url}
                              alt={item.alt_text || `Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleSubmit('draft')}
            disabled={createPostMutation.isPending}
          >
            Save Draft
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleSubmit('schedule')}
            disabled={createPostMutation.isPending || !formData.scheduled_time}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Schedule
          </Button>
          <Button 
            onClick={() => handleSubmit('publish')}
            disabled={createPostMutation.isPending || connectionHealth?.status === 'unhealthy'}
          >
            <Send className="h-4 w-4 mr-2" />
            {createPostMutation.isPending ? 'Publishing...' : 'Publish Now'}
          </Button>
        </div>
      </div>

      {/* BGV Gallery Selector Dialog */}
      <BGVGallerySelector
        open={showBGVGallery}
        onOpenChange={setShowBGVGallery}
        onImagesSelected={handleBGVImagesSelected}
        maxSelectionCount={10}
        initialSelectedImages={bgvImages}
      />
    </div>
  )
}